/**
 * One fetch per interval across every open browser tab. Before polling, a tab
 * claims a shared slot (a Web Lock where the browser has one, a localStorage
 * stamp either way); a tab that finds the slot freshly filled takes the value
 * another tab broadcast instead of asking the server. Each tab still polls only
 * while it is visible, so a lone leader never polls on behalf of hidden tabs.
 * Browser-only: the native app has one process and does not load this file.
 */

interface BroadcastLike {
  postMessage(message: unknown): void;
  addEventListener(type: 'message', listener: (event: { data: unknown }) => void): void;
  removeEventListener(type: 'message', listener: (event: { data: unknown }) => void): void;
  close(): void;
}

interface LocksLike {
  request<T>(name: string, callback: () => Promise<T>): Promise<T>;
}

export interface TabShareDeps {
  channel?: ((name: string) => BroadcastLike | null) | null;
  storage?: Pick<Storage, 'getItem' | 'setItem'> | null;
  locks?: LocksLike | null;
  now?: () => number;
  tabId?: string;
}

export interface TabShareOptions {
  /** Keep the value in localStorage so a tab opened later can use it. Only for small values. */
  persistValue?: boolean;
  /** How long a claim by another tab counts as still running. */
  claimTimeoutMs?: number;
}

export interface TabShare<T> {
  /**
   * Runs `task` unless some tab (this one included) finished it within
   * `freshMs`; then that value comes back with `fetched: false` and no request
   * is made. Pass 0 when something says the data has moved.
   */
  run(task: () => Promise<T>, freshMs: number): Promise<{ value: T; fetched: boolean }>;
  /** Values fetched by other tabs, as they arrive. */
  subscribe(listener: (value: T) => void): () => void;
  /** Hand a value this tab learned another way (realtime, a mutation) to the other tabs. */
  publish(value: T): void;
  close(): void;
}

type Stamp = { at: number; state: 'running' | 'done'; by: string; value?: unknown };
type Message = { kind: 'value'; at: number; by: string; value: unknown };

function browserStorage(): Pick<Storage, 'getItem' | 'setItem'> | null {
  // Reading localStorage throws when site data is blocked.
  try { return typeof window !== 'undefined' ? window.localStorage : null; } catch { return null; }
}

function defaultDeps(): Required<TabShareDeps> {
  const storage = browserStorage();
  const locks = typeof navigator !== 'undefined'
    ? ((navigator as Navigator & { locks?: LocksLike }).locks ?? null)
    : null;
  return {
    channel: typeof BroadcastChannel === 'function' ? (name: string) => new BroadcastChannel(name) : null,
    storage,
    locks,
    now: () => Date.now(),
    tabId: Math.random().toString(36).slice(2),
  };
}

export function createTabShare<T>(name: string, options: TabShareOptions = {}, deps: TabShareDeps = {}): TabShare<T> {
  const base = defaultDeps();
  const storage = deps.storage === undefined ? base.storage : deps.storage;
  const locks = deps.locks === undefined ? base.locks : deps.locks;
  const makeChannel = deps.channel === undefined ? base.channel : deps.channel;
  const now = deps.now ?? base.now;
  const tabId = deps.tabId ?? base.tabId;
  const claimTimeoutMs = options.claimTimeoutMs ?? 30_000;
  const key = `quantis:tab-share:${name}`;
  const listeners = new Set<(value: T) => void>();
  // Each waiter settles on a qualifying value, its timeout, or close().
  const waiters = new Set<{ wake: () => void; cancel: () => void }>();
  let closed = false;
  let latest: { at: number; value: T } | null = null;
  let channel: BroadcastLike | null = null;
  try { channel = makeChannel ? makeChannel(key) : null; } catch { channel = null; }

  const readStamp = (): Stamp | null => {
    try {
      const raw = storage?.getItem(key);
      return raw ? (JSON.parse(raw) as Stamp) : null;
    } catch {
      return null;
    }
  };
  const writeStamp = (stamp: Stamp) => {
    try { storage?.setItem(key, JSON.stringify(stamp)); } catch { /* storage full or blocked: tabs just stop sharing */ }
  };

  const accept = (at: number, value: T) => {
    if (latest && latest.at > at) return;
    latest = { at, value };
    for (const waiter of [...waiters]) waiter.wake();
  };

  const onMessage = (event: { data: unknown }) => {
    const message = event.data as Message | null;
    if (!message || message.kind !== 'value' || message.by === tabId) return;
    accept(message.at, message.value as T);
    for (const listener of [...listeners]) listener(message.value as T);
  };
  channel?.addEventListener('message', onMessage);

  // A stamp says another tab has it, but its broadcast may still be on the way.
  const waitForValue = (since: number, ms: number) => new Promise<{ at: number; value: T } | null>(resolve => {
    if (latest && latest.at >= since) { resolve(latest); return; }
    const settle = (value: { at: number; value: T } | null) => {
      clearTimeout(timer); waiters.delete(waiter); resolve(value);
    };
    const waiter = {
      wake: () => { if (latest && latest.at >= since) settle(latest); },
      cancel: () => settle(null),
    };
    const timer = setTimeout(() => settle(null), ms);
    waiters.add(waiter);
  });
  const closedError = () => new Error('This tab share was closed.');

  const fromStamp = (stamp: Stamp | null, freshMs: number, locked: boolean): { at: number; value: T } | null | 'wait' => {
    if (!stamp) return null;
    const age = now() - stamp.at;
    if (stamp.state === 'done' && age < freshMs) {
      if (latest && latest.at >= stamp.at) return latest;
      if ('value' in stamp) return { at: stamp.at, value: stamp.value as T };
      return stamp.by === tabId ? null : 'wait';
    }
    // Under a lock, a claim still marked running belongs to a tab that closed
    // mid-request; without one, another tab may be fetching right now.
    if (stamp.state === 'running' && stamp.by !== tabId && !locked && age < claimTimeoutMs) return 'wait';
    return null;
  };

  const fetchHere = async (task: () => Promise<T>) => {
    writeStamp({ at: now(), state: 'running', by: tabId });
    let value: T;
    try {
      value = await task();
    } catch (error) {
      // A failed claim must not keep the other tabs waiting for a value that never comes.
      writeStamp({ at: 0, state: 'done', by: tabId });
      throw error;
    }
    const at = now();
    accept(at, value);
    writeStamp(options.persistValue ? { at, state: 'done', by: tabId, value } : { at, state: 'done', by: tabId });
    try { channel?.postMessage({ kind: 'value', at, by: tabId, value } satisfies Message); } catch { /* an uncloneable value stays local */ }
    return value;
  };

  const attempt = async (task: () => Promise<T>, freshMs: number, locked: boolean) => {
    if (closed) throw closedError();
    const stamp = readStamp();
    const found = fromStamp(stamp, freshMs, locked);
    if (found && found !== 'wait') return { value: found.value, fetched: false };
    if (found === 'wait' && stamp) {
      const arrived = await waitForValue(stamp.at, stamp.state === 'running' ? claimTimeoutMs : 1_000);
      if (arrived) return { value: arrived.value, fetched: false };
      // An account switch closes the share mid-wait; it must not then fetch for the old account.
      if (closed) throw closedError();
    }
    return { value: await fetchHere(task), fetched: true };
  };

  return {
    run(task, freshMs) {
      // Holding the lock across the fetch makes a second tab wait and then find
      // the slot filled, rather than both tabs reading the same stale stamp.
      if (locks) return locks.request(key, () => attempt(task, freshMs, true));
      return attempt(task, freshMs, false);
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => { listeners.delete(listener); };
    },
    publish(value) {
      const at = now();
      accept(at, value);
      writeStamp(options.persistValue ? { at, state: 'done', by: tabId, value } : { at, state: 'done', by: tabId });
      try { channel?.postMessage({ kind: 'value', at, by: tabId, value } satisfies Message); } catch { /* local only */ }
    },
    close() {
      closed = true;
      channel?.removeEventListener('message', onMessage);
      channel?.close();
      channel = null;
      listeners.clear();
      for (const waiter of [...waiters]) waiter.cancel();
    },
  };
}
